import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import { useJobs } from '@/modules/jobs/hooks/useJobs';

import {
  openLoginDialog,
} from '@/modules/auth/store/authSlice';

import type { AuthRole } from '@/modules/auth/types/auth';

import { Button } from '@/shared/components/ui/button';

import CareerNavbar from '@/modules/Career/components/career/CareerNavbar';
import CareerHero from '@/modules/Career/components/career/CareerHero';
import CareerSearchBar from '@/modules/Career/components/career/CareerSearchBar';
import CareerCompanies from '@/modules/Career/components/career/CareerCompanies';
import CareerJobCard from '@/modules/Career/components/career/CareerJobCard';

import { companies } from '@/modules/Career/Data/careerData';

import type { Job } from '@/modules/jobs/types/job';

import {
  Building2,
  Briefcase,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface RootState {
  auth: {
    user: {
      id: string;
      name: string;
      email: string;
      role: AuthRole;
    } | null;
    isAuthenticated: boolean;
  };
}

export default function CareerPage() {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    user,
    isAuthenticated,
  } = useSelector(
    (state: RootState) => state.auth
  );

  const [searchQuery, setSearchQuery] =
    useState('');

  const [activeFilter, setActiveFilter] =
    useState<
      'ALL' | 'REMOTE' | 'URGENT'
    >('ALL');

  const searchInitializedRef =
    useRef(false);

  const filterInitializedRef =
    useRef(false);

  const {
    jobs,
    loading,
    pagination,
    updateFilters,
  } = useJobs({
    page: 1,
    limit: 6,
  });
  useEffect(() => {
    if (!searchInitializedRef.current) {
      searchInitializedRef.current = true;
      return;
    }

    const timer = setTimeout(() => {
      updateFilters({
        search: searchQuery,
        page: 1,
      });
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [
    searchQuery,
    updateFilters,
  ]);

  useEffect(() => {
    if (!filterInitializedRef.current) {
      filterInitializedRef.current = true;
      return;
    }

    switch (activeFilter) {
      case 'REMOTE':
        updateFilters({
          workplaceType: 'REMOTE',
          priority: '',
          page: 1,
        });
        break;

      case 'URGENT':
        updateFilters({
          priority: 'HIGH',
          workplaceType: '',
          page: 1,
        });
        break;

      default:
        updateFilters({
          workplaceType: '',
          priority: '',
          page: 1,
        });
        break;
    }
  }, [
    activeFilter,
    updateFilters,
  ]);

  const totalJobs = useMemo(() => {
    return pagination.total;
  }, [pagination.total]);

 
const handlePortalLogin = (requiredRole: AuthRole) => {
  if (isAuthenticated && user) {
    switch (user.role) {
      case 'admin':
        navigate('/admin');
        return;

      case 'hr':
      case 'recruiter':
      case 'operations':
      case 'manager':
        navigate('/hr');
        return;

      case 'candidate':
        navigate('/candidate');
        return;

      default:
        break;
    }
  }

  dispatch(
    openLoginDialog({
      mode: 'login',
      requiredRole,
    })
  );
};


  const nextPage = () => {
    if (
      pagination.page <
      pagination.totalPages
    ) {
      updateFilters({
        page: pagination.page + 1,
      });
    }
  };

  const previousPage = () => {
    if (pagination.page > 1) {
      updateFilters({
        page: pagination.page - 1,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <CareerNavbar
        onHRPortal={() =>
          handlePortalLogin('hr')
        }
        onAdminLogin={() =>
          handlePortalLogin('admin')
        }
        isAuthenticated={isAuthenticated}
        role={user?.role ?? null}
      />

      <CareerHero />

      <section className="border-y bg-slate-50/50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <CareerCompanies
            companies={companies}
          />
        </div>
      </section>

      <section
        id="open-positions"
        className="scroll-mt-16 py-12 lg:py-16"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-8 max-w-xl">
            <CareerSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2 text-center md:text-left">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Open Positions
              </h2>

              <p className="text-sm text-muted-foreground sm:text-base">
                {totalJobs} Open Positions
              </p>
            </div>

            <div className="flex w-full flex-wrap gap-2 md:w-auto">
              <Button
                variant={
                  activeFilter === 'ALL'
                    ? 'outline'
                    : 'ghost'
                }
                size="sm"
                className="flex-1 sm:flex-none"
                onClick={() =>
                  setActiveFilter('ALL')
                }
              >
                All Jobs
              </Button>

              <Button
                variant={
                  activeFilter === 'REMOTE'
                    ? 'outline'
                    : 'ghost'
                }
                size="sm"
                className="flex-1 sm:flex-none"
                onClick={() =>
                  setActiveFilter('REMOTE')
                }
              >
                Remote
              </Button>

              <Button
                variant={
                  activeFilter === 'URGENT'
                    ? 'outline'
                    : 'ghost'
                }
                size="sm"
                className="flex-1 sm:flex-none"
                onClick={() =>
                  setActiveFilter('URGENT')
                }
              >
                Urgent
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center">
              Loading jobs...
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {jobs.map(
                  (job: Job) => (
                    <CareerJobCard
                      key={job.id}
                      job={job}
                      onClick={() =>
                        navigate(
                          `/jobs/${job.id}`
                        )
                      }
                    />
                  )
                )}
              </div>

              {jobs.length === 0 && (
                <div className="py-12 text-center">
                  <Briefcase className="mx-auto h-12 w-12 text-muted-foreground" />

                  <h3 className="mt-4 text-lg font-semibold">
                    No positions found
                  </h3>

                  <p className="text-muted-foreground">
                    Try adjusting your search criteria
                  </p>
                </div>
              )}

              {pagination.totalPages > 1 && (
                <div className="mt-12 flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.page} of{' '}
                    {pagination.totalPages}
                  </p>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      onClick={
                        previousPage
                      }
                      disabled={
                        pagination.page ===
                        1
                      }
                    >
                      <ChevronLeft className="mr-2 h-4 w-4" />
                      Previous
                    </Button>

                    <Button
                      variant="outline"
                      onClick={nextPage}
                      disabled={
                        pagination.page ===
                        pagination.totalPages
                      }
                    >
                      Next
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <footer className="border-t bg-slate-50 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 lg:flex-row lg:px-8">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />

            <span className="font-semibold">
              Viraaj Ventures
            </span>
          </div>

          <p className="text-sm text-muted-foreground">
            AI-Powered Recruitment Platform |{' '}
            {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
}